import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Globe } from 'lucide-react';
import { useLanguage, Language } from '../../contexts/LanguageContext';

const Header: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);
  const { language, setLanguage, t } = useLanguage();
  const location = useLocation();

  // Pages avec hero sombre (texte blanc par defaut)
  const isDarkHero = location.pathname === '/' || location.pathname.match(/^\/[a-z]{2}$/);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Sur les pages a fond blanc, forcer le style "scrolled" (texte noir)
  const useDarkText = isScrolled || !isDarkHero;

  const navigation = [
    { name: t('common.navigation.home'), href: '/' },
    { name: t('common.navigation.services'), href: '/services' },
    { name: t('common.navigation.tires'), href: '/pneus' },
    { name: t('common.navigation.gallery'), href: '/gallery' },
    { name: t('common.navigation.faq'), href: '/faq' },
    { name: t('common.navigation.contact'), href: '/contact' },
  ];

  const languages: { code: Language; name: string }[] = [
    { code: 'fr', name: t('common.languages.fr') },
    { code: 'de', name: t('common.languages.de') },
    { code: 'en', name: t('common.languages.en') },
    { code: 'pt', name: t('common.languages.pt') },
  ];

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        useDarkText
          ? 'bg-white/95 backdrop-blur-lg shadow-lg py-4'
          : 'bg-transparent py-6'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <motion.div
            whileHover={{ scale: 1.05 }}
            className="flex-shrink-0 ml-8"
          >
            <Link to="/">
             <div className="bg-white rounded-full p-2 shadow-lg">
                <img 
                  src="/Autocat-Logo.png" 
                  alt={t('common.company.name')}
                 className="h-12 w-auto"
                  loading="lazy"
                />
              </div>
            </Link>
          </motion.div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
              >
                <motion.div
                  whileHover={{ scaleX: 1 }}
                  className={`relative font-medium transition-colors duration-200 ${
                    useDarkText ? 'text-gray-900 hover:text-red-600' : 'text-white hover:text-red-400'
                  } ${
                    location.pathname === item.href ? 'text-red-600' : ''
                  }`}
                >
                  {item.name}
                  <motion.div
                    className="absolute -bottom-1 left-0 h-0.5 bg-red-600"
                    whileHover={{ scaleX: 1 }}
                    initial={{ scaleX: location.pathname === item.href ? 1 : 0 }}
                    transition={{ duration: 0.2 }}
                  />
                </motion.div>
              </Link>
            ))}
          </nav>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center space-x-4">
            {/* Language Selector */}
            <div className="relative">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setIsLanguageMenuOpen(!isLanguageMenuOpen)}
                aria-label="Choisir la langue"
                className={`flex items-center space-x-1 px-3 py-2 rounded-lg transition-colors ${
                  useDarkText
                    ? 'text-gray-700 hover:bg-gray-100'
                    : 'text-white hover:bg-white/10'
                }`}
              >
                <Globe className="w-4 h-4" />
                <span className="uppercase text-sm font-medium">{language}</span>
              </motion.button>

              <AnimatePresence>
                {isLanguageMenuOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute top-full right-0 mt-2 bg-white rounded-lg shadow-lg border py-1 min-w-[120px]"
                  >
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => {
                          setLanguage(lang.code);
                          setIsLanguageMenuOpen(false);
                        }}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${
                          language === lang.code
                            ? 'text-red-600 font-medium'
                            : 'text-gray-700'
                        }`}
                      >
                        {lang.name}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Contact Button (appears after scroll) */}
            <AnimatePresence>
              {useDarkText && (
                <motion.a
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  href="/contact"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="flex items-center space-x-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
                >
                  <Phone className="w-4 h-4" />
                  <span>{t('common.cta.contact')}</span>
                </motion.a>
              )}
            </AnimatePresence>

            {/* Custom Request Button */}
            <motion.a
              href="/custom-request"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={`px-4 py-2 rounded-lg border-2 font-medium transition-all duration-200 ${
                useDarkText
                  ? 'border-red-600 text-red-600 hover:bg-red-600 hover:text-white'
                  : 'border-white text-white hover:bg-white hover:text-gray-900'
              }`}
            >
              {t('common.navigation.customRequest')}
            </motion.a>
          </div>

          {/* Mobile Menu Button */}
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
            className={`md:hidden p-2 rounded-lg transition-colors ${
              useDarkText
                ? 'text-gray-900 hover:bg-gray-100'
                : 'text-white hover:bg-white/10'
            }`}
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </motion.button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white border-t"
          >
            <div className="px-4 py-6 space-y-4">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`block font-medium transition-colors ${
                    location.pathname === item.href 
                      ? 'text-red-600' 
                      : 'text-gray-900 hover:text-red-600'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
              
              <div className="pt-4 border-t">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-500">
                    {t('common.languages.fr')}
                  </span>
                  <div className="flex space-x-2">
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => {
                          setLanguage(lang.code);
                          setIsMobileMenuOpen(false);
                        }}
                        className={`px-2 py-1 text-xs rounded transition-colors ${
                          language === lang.code
                            ? 'bg-red-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {lang.code.toUpperCase()}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="pt-4 space-y-3">
                <a
                  href="/contact"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center justify-center space-x-2 w-full bg-red-600 text-white py-3 rounded-lg hover:bg-red-700 transition-colors"
                >
                  <Phone className="w-4 h-4" />
                  <span>{t('common.cta.contact')}</span>
                </a>
                <a
                  href="/custom-request"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block text-center w-full border-2 border-red-600 text-red-600 py-3 rounded-lg hover:bg-red-600 hover:text-white transition-all"
                >
                  {t('common.navigation.customRequest')}
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Header;